/* Bernard James — Contact page, live chat card (sections/bj-contact-methods.liquid).

   The CHAT card ships in the Figma offline state: a flat grey
   "CHAT UNAVAILABLE" button with aria-disabled. This script turns it on
   only when BOTH hold:
     1. the current time, in the card's time zone, falls inside the
        configured days and hours (data-days / data-open / data-close /
        data-timezone), and
     2. a live-chat widget can be opened — window.bjOpenAdvisorChat from
        assets/bj-pdp-advisor-chat.js returns true for HubSpot
        Conversations, Shopify Inbox, Gorgias, Tidio, Zendesk or Intercom —
        or the card has a data-fallback-href to send the visitor to.

   As of 2026-10-22 bernardjames.com loads HubSpot's Conversations embed but
   no chatflow targets the site (HubSpot answers HIDE_WIDGET), so the widget
   never loads and the button stays unavailable until the merchant publishes
   one. Nothing else on the page depends on this. */
(function () {
  'use strict';

  var SELECTOR = '[data-bj-contact-chat]';
  var RECHECK_MS = 3000; // widgets load late; look again once

  function parseDays(spec) {
    var days = {};
    String(spec || '1-5')
      .split(',')
      .forEach(function (part) {
        part = part.trim();
        if (!part) return;
        var range = part.split('-');
        var from = parseInt(range[0], 10);
        var to = parseInt(range[range.length - 1], 10);
        if (isNaN(from) || isNaN(to)) return;
        for (var d = Math.min(from, to); d <= Math.max(from, to); d++) days[d] = true;
      });
    return days;
  }

  function parseMinutes(hhmm, fallback) {
    var m = /^(\d{1,2})(?::(\d{2}))?/.exec(String(hhmm || '').trim());
    if (!m) return fallback;
    return parseInt(m[1], 10) * 60 + (m[2] ? parseInt(m[2], 10) : 0);
  }

  // Current weekday (1 = Monday … 7 = Sunday) and minutes since midnight
  // in the given IANA zone; falls back to the visitor's clock when Intl
  // cannot resolve the zone.
  function nowIn(timeZone) {
    var now = new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(now);
      var map = {};
      parts.forEach(function (p) {
        map[p.type] = p.value;
      });
      var weekdays = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
      var hour = parseInt(map.hour, 10) % 24;
      return { day: weekdays[map.weekday] || 7, minutes: hour * 60 + parseInt(map.minute, 10) };
    } catch (e) {
      var day = now.getDay() === 0 ? 7 : now.getDay();
      return { day: day, minutes: now.getHours() * 60 + now.getMinutes() };
    }
  }

  function withinHours(button) {
    var days = parseDays(button.getAttribute('data-days'));
    var open = parseMinutes(button.getAttribute('data-open'), 10 * 60);
    var close = parseMinutes(button.getAttribute('data-close'), 18 * 60);
    var t = nowIn(button.getAttribute('data-timezone') || 'America/New_York');
    return !!days[t.day] && t.minutes >= open && t.minutes < close;
  }

  function hubspotLoaded() {
    // The embed object is present whenever Conversations is installed; the
    // widget only loads when a chatflow targets this page (see
    // bj-pdp-advisor-chat.js). An unloaded widget cannot be opened.
    var w = window.HubSpotConversations && window.HubSpotConversations.widget;
    if (!w || typeof w.status !== 'function') return false;
    var status = w.status();
    return !!(status && status.loaded === true);
  }

  function widgetAvailable() {
    return !!(
      hubspotLoaded() ||
      document.querySelector('inbox-online-store-chat') ||
      window.GorgiasChat ||
      window.tidioChatApi ||
      (window.zE && typeof window.zE === 'function') ||
      (window.Intercom && typeof window.Intercom === 'function')
    );
  }

  function setState(button, online) {
    var label = button.querySelector('span') || button;
    var card = button.closest('.bj-ct-card');
    var text = card && card.querySelector('[data-bj-contact-chat-text]');
    if (online) {
      button.removeAttribute('aria-disabled');
      label.textContent = button.getAttribute('data-online-label') || label.textContent;
      if (text && text.getAttribute('data-online-text')) {
        if (!text.hasAttribute('data-offline-text')) text.setAttribute('data-offline-text', text.textContent);
        text.textContent = text.getAttribute('data-online-text');
      }
    } else {
      button.setAttribute('aria-disabled', 'true');
      label.textContent = button.getAttribute('data-offline-label') || label.textContent;
      if (text && text.hasAttribute('data-offline-text')) text.textContent = text.getAttribute('data-offline-text');
    }
    button.classList.toggle('is-online', online);
  }

  function evaluate(button) {
    var online = withinHours(button) && (widgetAvailable() || !!button.getAttribute('data-fallback-href'));
    setState(button, online);
    return online;
  }

  function init() {
    var buttons = Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
    if (!buttons.length) return;
    buttons.forEach(evaluate);
    window.setTimeout(function () {
      buttons.forEach(evaluate);
    }, RECHECK_MS);
    // Re-evaluate on the minute so the state flips at opening/closing time.
    window.setInterval(function () {
      buttons.forEach(evaluate);
    }, 60000);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest(SELECTOR);
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;
    var opened = typeof window.bjOpenAdvisorChat === 'function' && window.bjOpenAdvisorChat({ source: 'contact' });
    if (opened) return;
    var href = button.getAttribute('data-fallback-href');
    if (href) window.location.href = href;
    else setState(button, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: sections re-render on setting changes.
  document.addEventListener('shopify:section:load', init);
})();
